import * as React from "react";
import { Link, useMediaQuery } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { useNavigate, useLocation } from "react-router-dom";

// Zustand store
import { useAppStore } from "../../stores";

// needed components
import GenericTabs from "components/GenericTabs/GenericTabs";
import UserInformation from "components/UserInformation/UserInformation";

// import services
import { getAffiliateSummary } from "./services";
import GenericSpinner from "../../components/GenericSpinner/GenericSpinner";

interface IAttendeeSummary {
  firstName?: string;
  lastName?: string;
  memberId?: string;
  address1?: string;
  city?: string;
  stateCode?: string;
  zip?: string;
  contactEmail?: string;
  ccEmail?: string;
  phone?: string;
  credentialType?: string;
  localNumber?: string;
  reportedDate?: string;
  isDelegate?: boolean;
  committeeChoice1Name?: string;
  committeeChoice2Name?: string;
  committeeChoice3Name?: string;
  badgeNumber?: number;
  affiliateName?: string;
  affiliateNumber?: string;
  modifiedDate?: string;
  affiliate?: {
    affiliateNumber?: string;
    affiliateName?: string;
  };
  credentialTypeId?: string;
  confirmationNumber?: string;
  inviteeStatus?: string;
  committeeChoice1?: number;
  committeeChoice2?: number;
  committeeChoice3?: number;
  repAffiliateId?: number;
  repAffiliateGuid?: string;
  homeAffiliateGuid?: string;
  parentAffiliate?: {
    affiliateNumber?: string;
    affiliateName?: string;
  };
  createdDate?: string;
}

interface IAttendeeSummaryProps {}

const AttendeeSummary: React.FC<IAttendeeSummaryProps> = () => {
  const displayMobile = useMediaQuery("(max-width:959px)");

  const navigate = useNavigate();
  const location = useLocation();

  // Zustand store
  const attendeeSummaryRequestPending = useAppStore(
    (state) => state.attendeeSummary?.attendeeSummaryPending || false
  );
  const attendeeSummary = useAppStore(
    (state) => state.attendeeSummary?.attendeeSummaryData as IAttendeeSummary | null
  );
  const setAttendeeSummaryRequestPending = useAppStore(
    (state) => state.setAttendeeSummaryPending
  );
  const setAttendeeSummary = useAppStore((state) => state.setAttendeeSummaryData);
  const setAttendeeSummaryRequestSuccessful = useAppStore(
    (state) => state.setAttendeeSummaryPending
  );

  // Watch for attendee record changes to detect credential type updates
  const currentAttendeeRecord = useAppStore((state) => state.attendee?.attendeeRecord);
  const lastCredentialTypeRef = React.useRef<string | undefined>(
    (currentAttendeeRecord as any)?.credentialType
  );

  React.useEffect(() => {
    setAttendeeSummaryRequestPending(true);
    const attendeeId = location.pathname.split("/")[3];
    getAffiliateSummary(Number(attendeeId)).then(
      (response: { data?: unknown }) => {
        if (response.data) {
          const { data } = response;
          setAttendeeSummary(data);
          setAttendeeSummaryRequestSuccessful(false);
        }
      }
    );
  }, []);

  // Refetch attendee summary when credential type changes
  React.useEffect(() => {
    const currentCredentialType = (currentAttendeeRecord as any)?.credentialType;
    
    if (
      currentCredentialType && 
      lastCredentialTypeRef.current && 
      currentCredentialType !== lastCredentialTypeRef.current
    ) {
      // Credential type changed, refetch the summary
      setAttendeeSummaryRequestPending(true);
      const attendeeId = location.pathname.split("/")[3];
      getAffiliateSummary(Number(attendeeId)).then(
        (response: { data?: unknown }) => {
          if (response.data) {
            const { data } = response;
            setAttendeeSummary(data);
            setAttendeeSummaryRequestSuccessful(false);
          }
        }
      );
      
      // Update the ref to the new credential type
      lastCredentialTypeRef.current = currentCredentialType;
    }
  }, [currentAttendeeRecord]);

  const attendeeGeneralInformation = () => {
    if (attendeeSummary) {
      return (
        <Grid size={{ xs: 12, md: 6, lg: 8 }} className="pt-3">
          <Grid container className={"attendee-information"}>
            <Grid container className={"attendee-breakpoint"}>
              <Grid size={{ xs: 12, sm: 12, lg: 6 }}>
                <Grid size={{ xs: 12, md: 12 }}>
                  <span className="affiliate-detail-label">Name: </span>
                  <span className="affiliate-detail-value">
                    {`${attendeeSummary.firstName} ${attendeeSummary.lastName}`}
                  </span>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                  <span className="affiliate-detail-label">Email: </span>
                  <span className="affiliate-detail-value">
                    {attendeeSummary.contactEmail}
                  </span>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                  <span className="affiliate-detail-label">Phone: </span>
                  <span className="affiliate-detail-value">
                    {attendeeSummary.phone &&
                      attendeeSummary.phone.replace(
                        /(\d{3})(\d{3})(\d{4})/,
                        "($1) $2-$3"
                      )}
                  </span>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                  <span className="affiliate-detail-label">Address: </span>
                  <span className="affiliate-detail-value">
                    {attendeeSummary.address1}
                  </span>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                  <span className="affiliate-detail-label">
                    City, State & Zip:{" "}
                  </span>
                  <span className="affiliate-detail-value">{`${attendeeSummary.city}, ${attendeeSummary.stateCode} ${attendeeSummary.zip}`}</span>
                </Grid>
              </Grid>
              <Grid size={{ xs: 12, sm: 12, lg: 6 }}>
                <Grid size={{ xs: 12, md: 12 }}>
                  <span className="affiliate-detail-label">Member ID: </span>
                  <span className="affiliate-detail-value">
                    {attendeeSummary.memberId}{" "}
                  </span>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                  <span className="affiliate-detail-label">Type: </span>
                  <span className="affiliate-detail-value">{attendeeSummary.credentialType}</span>
                </Grid>

                <Grid size={{ xs: 12, md: 12 }}>
                  <span className="affiliate-detail-label">Local Number: </span>
                  <span className="affiliate-detail-value">
                    {attendeeSummary.localNumber}
                  </span>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                  <span className="affiliate-detail-label">Reported Date: </span>
                  <span className="affiliate-detail-value">
                    {attendeeSummary.reportedDate ? attendeeSummary.reportedDate.split("T")[0]: "n/a"}
                  </span>
                </Grid>
              </Grid>
            </Grid>
            {attendeeSummary.isDelegate && (<Grid container className={"attendee-breakpoint"}>
              <Grid size={{ xs: 12, md: 12 }}>
                <span className="affiliate-detail-label">
                  Committee Choices
                </span>
              </Grid>
              <Grid size={{ xs: 12, md: 12 }}>
                <ol className={"pl-3"}>
                  {attendeeSummary.committeeChoice1Name !== "" && (
                    <Grid size={{ xs: 12, md: 12 }}>
                      <li className="affiliate-detail-value">
                        <span>{attendeeSummary.committeeChoice1Name}</span>
                      </li>
                    </Grid>
                  )}
                  {attendeeSummary.committeeChoice2Name !== "" && (
                    <Grid size={{ xs: 12, md: 12 }}>
                      <li className="affiliate-detail-value">
                        <span>{attendeeSummary.committeeChoice2Name}</span>
                      </li>
                    </Grid>
                  )}
                  {attendeeSummary.committeeChoice3Name !== "" && (
                    <Grid size={{ xs: 12, md: 12 }}>
                      <li className="affiliate-detail-value">
                        <span>{attendeeSummary.committeeChoice3Name}</span>
                      </li>
                    </Grid>
                  )}
                </ol>
              </Grid>
            </Grid>)}
          </Grid>
        </Grid>
      );
    }
    return null;
  };

  const userCard = attendeeSummary ? (
    <Grid
      size={{ xs: 12, md: 6, lg: 4 }}
      className={`${displayMobile ? "pl-0" : "pl-4"}`}
    >
      <UserInformation user={attendeeSummary as IAttendeeSummary} />
    </Grid>
  ) : null;

  const renderProperInformationBasedOnMobile = () => {
    if (displayMobile) {
      return (
        <Grid size={{ xs: 12 }} className={"pt-2"}>
          <GenericTabs
            className={"mobile-tabs"}
            tabs={[
              {
                name: "Attendee Info",
                className: "mobile-tab",
              },
              {
                name: "Attendee Card",
                className: "mobile-tab",
              },
            ]}
            tabContent={[
              {
                content: attendeeGeneralInformation(),
              },
              {
                content: userCard,
              },
            ]}
          />
        </Grid>
      );
    } else {
      return (
        <Grid container size={{ xs: 12 }}>
          {attendeeGeneralInformation()}
          {userCard}
        </Grid>
      );
    }
  };

  return (
    <Grid container size={{ xs: 12 }}>
      <Grid size={{ xs: 12 }}>
        {attendeeSummaryRequestPending ? (
          <GenericSpinner includeOverlay={true} padding={25} />
        ) : (
            <Grid container size={{ xs: 12 }}>
              <Grid size={{ xs: 12 }}>
                <h2>Attendee Summary</h2>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Link className={"back-btn"} onClick={() => navigate(-1)} style={{ cursor: 'pointer' }}>
                  {"< "}Back to Affiliate Summary
              </Link>
              </Grid>
              <Grid size={{ xs: 12 }}>
                {renderProperInformationBasedOnMobile()}
              </Grid>
            </Grid>
          )}
      </Grid>
    </Grid>
  );
};

export default AttendeeSummary;
